import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { MediaAuthContext } from "../../../store/context/media-auth-context";
import { SimpleContext } from "../../../store/context/simple-context";
import {
  PERIODS,
  AVAILABLE_OPTIONS,
  AVAILABLE_TARIFFS,
  MODES,
  REFS_HIDE_TABLE,
  TABLE_PERIODS,
} from "../../../components/Paywalls/Customizable/content/index.content";
import { ADDITIONAL_LANGUAGES_CHECKOUT } from "../../../helpers/constants";
import EN from "../../../locales/en/customizable.json";
import DE from "../../../locales/de/customizable.json";
import CN from "../../../locales/cn/customizable.json";
import JA from "../../../locales/ja/customizable.json";
import ES from "../../../locales/es/customizable.json";
import FR from "../../../locales/fr/customizable.json";
import track from "../../../tracking/analytics";
import { trackGa } from "../../../tracking/googleAnalytics";
import Customizable from "../../../components/Paywalls/Customizable";
import useLocation from "../../../hooks/useLocation";

const resources = {
  en: { translation: EN },
  de: { translation: DE },
  cn: { translation: CN },
  ja: { translation: JA },
  es: { translation: ES },
  fr: { translation: FR },
};

// v5
const CustomizableContainer = (props) => {
  const {
    availableTariffs = AVAILABLE_TARIFFS,
    availableOptions = AVAILABLE_OPTIONS,
    advancedPrivateLabel = false,
    advancedReselling = false,
    mode,
    customClass,
    paramsLink = "",
    language = "en",
    modalRef,
    pricingRef,
    software = "LANDING",
    startPeriod = PERIODS[2],
    setIsDealsHidesPrice,
  } = props;
  const LANGUAGE = useMemo(
    () => (resources[language] ? language : "en"),
    [language]
  );
  const isModal = mode === MODES.modal;
  const isIframe = mode === MODES.iframe;
  const isPricingPage = mode === MODES.pricingPage;
  const ENABLED_DEALS_SECTION = process.env.DEALS_SECTION === "true";
  const {
    media: { isNotInitMobile, isTabletOrLess },
  } = useContext(MediaAuthContext);
  const [position, setPosition] = useState({
    bottom: false,
    sticky: false,
  });
  const [getState, setState] = useState({
    currentPeriod: null,
    table: {
      common: {
        open: false,
        focus: false,
      },
      monthly: {
        open: false,
        focus: false,
      },
      monthly_3: {
        open: false,
        focus: false,
      },
      yearly: {
        open: false,
        focus: false,
      },
      lifetime: {
        open: false,
        focus: false,
      },
    },
    showHelpSpinner: false,
    options: {
      PL: advancedPrivateLabel,
      R: advancedReselling,
    },
  });

  useEffect(() => {
    updateState({ currentPeriod: isTabletOrLess ? null : startPeriod });
  }, [isTabletOrLess]);

  const { countryCode } = useLocation();

  const toggleRef = useRef(null);
  const tableRef = useRef(null);
  const monthlyNavRef = useRef(null);
  const monthly_3NavRef = useRef(null);
  const yearlyNavRef = useRef(null);
  // const lifetimeNavRef = useRef(null);
  const optionsRef = useRef(null);
  const stickyBlockRef = useRef(null);
  const containerRef = useRef(null);
  const refs = {
    pricingRef,
    toggleRef,
    tableRef,
    monthlyNavRef,
    monthly_3NavRef,
    yearlyNavRef,
    // lifetimeNavRef,
    optionsRef,
    stickyBlockRef,
    containerRef,
  };

  i18n.init({
    lng: LANGUAGE,
    resources,
  });
  const t = i18n.t;
  const eventCategory = `${software}|Pricing`;

  const updateState = (args) => {
    setState((prevState) => ({
      ...prevState,
      ...args,
    }));
  };
  const updatePosition = (args) => {
    setPosition((prevState) => ({
      ...prevState,
      ...args,
    }));
  };
  const updateOptionsState = (args) => {
    setState((prevState) => ({
      ...prevState,
      options: {
        ...prevState.options,
        ...args,
      },
    }));
  };

  const updateTableState = (key, args) => {
    setState((prevState) => ({
      ...prevState,
      table: {
        ...prevState.table,
        [key]: {
          ...prevState.table[key],
          ...args,
        },
      },
    }));
  };

  const updateLink = (link) => {
    paramsLink && (link += paramsLink);
    return ADDITIONAL_LANGUAGES_CHECKOUT.includes(LANGUAGE)
      ? `/${LANGUAGE}${link}`
      : link;
  };
  const sendEventOnNavigation = (label) => {
    const action = "Select";
    track(eventCategory, action, label);
    trackGa(action, {
      category: eventCategory,
      label,
      transport_type: "beacon",
    });
  };
  const focusOnOptions = () => {
    setTimeout(() => {
      if (optionsRef) {
        switch (true) {
          case isIframe: {
            const rect = optionsRef.current.getBoundingClientRect();
            const scrollPosition = window.pageYOffset;
            requestAnimationFrame(() => {
              window.scrollTo({
                top: rect.top + scrollPosition,
                behavior: "smooth",
              });
            });
            break;
          }
          case ENABLED_DEALS_SECTION && !isModal && isNotInitMobile: {
            stickyBlockRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            break;
          }
          default: {
            optionsRef.current.scrollIntoView({
              behavior: "smooth",
              block: isModal ? "start" : "center",
            });
          }
        }
      }
    }, 100);
  };
  const focusOnTable = (key) => {
    const tableRef = refs[REFS_HIDE_TABLE[key]];
    if (tableRef) {
      tableRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };
  const tableOpenHandler = (key, status) => {
    updateTableState(key, {
      open: status,
      ...(!status && { focus: true }),
    });
  };
  const navigationHandler = (e, period) => {
    // if (getState.currentPeriod !== period) {
    if (getState.currentPeriod !== period && !e.target.closest(".GP-C-Q")) {
      updateState({ currentPeriod: period });
      sendEventOnNavigation(period);
      // period !== PERIODS[2] && focusOnOptions();
      focusOnOptions();
    }
  };

  const scrollHandler = () => {
    if (stickyBlockRef) {
      const container = containerRef.current?.getBoundingClientRect();
      const stickyBlock = stickyBlockRef.current?.getBoundingClientRect();
      let topMarker = 84;
      let top = container.top - 64 <= 0;
      if (isModal) {
        const modal = modalRef.current?.getBoundingClientRect();
        topMarker = 0;
        top = container.top <= modal.top;
      }
      if (isIframe || isPricingPage) {
        topMarker = 20;
        top = container.top <= 0;
      }
      const bottom = container.bottom - (stickyBlock.height + topMarker) > 0;
      const sticky = top && bottom;
      updatePosition({ bottom: !bottom, sticky });
    }
    if (setIsDealsHidesPrice) {
      const pricing = pricingRef.current.getBoundingClientRect();
      const innerHeight = window.innerHeight;
      const top = innerHeight - pricing.top - 90 > 0;
      const bottom = innerHeight - pricing.bottom < 0;
      setIsDealsHidesPrice(top && bottom);
    }
  };

  useEffect(() => {
    TABLE_PERIODS.forEach((key) => {
      updateTableState(key, { open: false });
    });
  }, [isNotInitMobile]);

  useEffect(() => {
    scrollHandler();
  }, [
    getState.currentPeriod,
    getState.table.common.open,
    getState.table.monthly.open,
    getState.table.monthly_3.open,
    getState.table.yearly.open,
    // getState.table.lifetime.open,
    getState.options.PL,
    getState.options.R,
  ]);

  useEffect(() => {
    TABLE_PERIODS.forEach((key) => {
      if (getState.table[key].focus) {
        focusOnTable(key);
        updateTableState(key, { focus: false });
      }
    });
  }, [
    getState.table.common.focus,
    getState.table.monthly.focus,
    getState.table.monthly_3.focus,
    getState.table.yearly.focus,
    // getState.table.lifetime.focus,
  ]);

  useEffect(() => {
    if (isModal) {
      modalRef.current.addEventListener("scroll", scrollHandler);
    }
    window.addEventListener("scroll", scrollHandler);
    window.addEventListener("resize", scrollHandler);
    return () => {
      if (!isModal) {
        window.removeEventListener("resize", scrollHandler);
        window.removeEventListener("scroll", scrollHandler);
      }
    };
  }, []);
  return (
    <SimpleContext.Provider
      value={{
        data: {
          currentPeriod: getState.currentPeriod,
          options: availableOptions[getState.currentPeriod || PERIODS[1]],
          numberOfOptions:
            availableOptions[getState.currentPeriod || PERIODS[1]].length,
          optionsState: getState.options,
          currentTariff: availableTariffs[getState.currentPeriod || PERIODS[1]],
          availableTariffs: availableTariffs,
          tableState: getState.table,
          eventCategory,
        },
        modes: {
          mode,
          customClass,
        },
        translate: {
          countryCode,
          language: LANGUAGE,
          t,
        },
        update: {
          updateLink,
          updateOptionsState,
          tableOpenHandler,
          navigationHandler,
        },
      }}
    >
      <Customizable refs={refs} position={position} />
    </SimpleContext.Provider>
  );
};
CustomizableContainer.propTypes = {
  availableTariffs: PropTypes.object,
  availableOptions: PropTypes.object,
  advancedBoosterPackDefault: PropTypes.bool,
  individualReportDefault: PropTypes.bool,
  mode: PropTypes.string,
  customClass: PropTypes.string,
  getParams: PropTypes.string,
  hideStickyBlock: PropTypes.bool,
  language: PropTypes.string,
  modalRef: PropTypes.object,
  pricingRef: PropTypes.object,
  startPeriod: PropTypes.string,
};
export default CustomizableContainer;
